import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Calculator, Target, DollarSign, PieChart, BarChart3, Info, Building, Banknote, Star } from 'lucide-react';

interface DataPoint {
  annee: number;
  valeur: number;
  capitalInvesti: number;
}

type ProduitType = 'scpi' | 'gfi' | 'sukuk';

interface Produit {
  id: ProduitType;
  nom: string;
  description: string;
  rendement: number;
  risque: 'Faible' | 'Modéré' | 'Élevé';
  liquidite: string;
  icon: React.ReactNode;
  couleur: string;
  couleurBg: string;
  avantages: string[];
}

const produits: Produit[] = [
  {
    id: 'scpi',
    nom: 'SCPI',
    description: 'Société Civile de Placement Immobilier - Investissement immobilier mutualisé',
    rendement: 0.045,
    risque: 'Modéré',
    liquidite: '5-8 ans',
    icon: <Building className="w-6 h-6 text-white" />,
    couleur: 'from-blue-500 to-indigo-600',
    couleurBg: 'from-blue-50 to-indigo-50',
    avantages: ['Revenus réguliers', 'Gestion déléguée', 'Diversification géographique']
  },
  {
    id: 'gfi',
    nom: 'GFI',
    description: 'Gestion de Fortune Internationale - Placement diversifié haut de gamme',
    rendement: 0.055,
    risque: 'Modéré',
    liquidite: '5-8 ans',
    icon: <Banknote className="w-6 h-6 text-white" />,
    couleur: 'from-emerald-500 to-green-600',
    couleurBg: 'from-emerald-50 to-green-50',
    avantages: ['Rendements attractifs', 'Gestion professionnelle', 'Diversification internationale']
  },
  {
    id: 'sukuk',
    nom: 'Sukuk',
    description: 'Obligation islamique - Investissement éthique conforme à la Charia',
    rendement: 0.06,
    risque: 'Faible',
    liquidite: '2-3 ans',
    icon: <Star className="w-6 h-6 text-white" />,
    couleur: 'from-amber-500 to-orange-600',
    couleurBg: 'from-amber-50 to-orange-50',
    avantages: ['Finance islamique', 'Éthique et transparent', 'Risque maîtrisé']
  }
];

const SimulateurProduits: React.FC = () => {
  const [produitSelectionne, setProduitSelectionne] = useState<ProduitType>('scpi');
  const [capitalInitial, setCapitalInitial] = useState<number>(10000);
  const [versementMensuel, setVersementMensuel] = useState<number>(200);
  const [dureeAnnees, setDureeAnnees] = useState<number>(10);
  const [avecVersementProgramme, setAvecVersementProgramme] = useState<boolean>(true);
  const [data, setData] = useState<DataPoint[]>([]);

  const produit = produits.find(p => p.id === produitSelectionne)!;

  const calculerProjection = () => {
    const nouvelleDonnee: DataPoint[] = [];
    const tauxRendement = produit.rendement;
    
    for (let annee = 0; annee <= dureeAnnees; annee++) {
      // Capital initial avec rendement
      let valeur = capitalInitial * Math.pow(1 + tauxRendement, annee);
      let capitalTotalInvesti = capitalInitial;
      
      if (avecVersementProgramme && annee > 0) {
        // Formule pour rente avec capitalisation
        const versementAnnuel = versementMensuel * 12;
        const valeurVersements = versementAnnuel * ((Math.pow(1 + tauxRendement, annee) - 1) / tauxRendement);
        valeur += valeurVersements;
        capitalTotalInvesti += versementAnnuel * annee;
      }
      
      nouvelleDonnee.push({
        annee,
        valeur: Math.round(valeur),
        capitalInvesti: Math.round(capitalTotalInvesti)
      });
    }
    
    setData(nouvelleDonnee);
  };

  useEffect(() => {
    calculerProjection();
  }, [produitSelectionne, capitalInitial, versementMensuel, dureeAnnees, avecVersementProgramme]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  const resultatsFinaux = data[data.length - 1];
  const totalInvesti = resultatsFinaux ? resultatsFinaux.capitalInvesti : capitalInitial;
  const gain = resultatsFinaux ? resultatsFinaux.valeur - resultatsFinaux.capitalInvesti : 0;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #fafbfc 0%, #f1f5f9 50%, #e2e8f0 100%)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center bg-white border border-gray-200 shadow-sm text-gray-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <div className="w-2 h-2 bg-purple-500 rounded-full mr-2 animate-pulse"></div>
            Simulateur de Produits d'Investissement
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
            Choisissez votre{' '}
            <span style={{ 
              background: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent'
            }}>
              Produit d'Investissement
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto font-light">
            Comparez les rendements entre SCPI, GFI et Sukuk selon vos objectifs financiers
          </p>
        </div>

        {/* Sélection de Produit */}
        <div className="mb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {produits.map((prod) => (
              <div
                key={prod.id}
                onClick={() => setProduitSelectionne(prod.id)}
                className={`cursor-pointer rounded-2xl p-6 border-2 transition-all duration-300 hover:shadow-xl ${
                  produitSelectionne === prod.id
                    ? 'border-purple-500 shadow-lg transform scale-105'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                style={{
                  background: produitSelectionne === prod.id
                    ? `linear-gradient(135deg, ${prod.couleurBg.split(' ')[1]} 0%, ${prod.couleurBg.split(' ')[3]} 100%)`
                    : 'white'
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg bg-gradient-to-r ${prod.couleur}`}>
                    {prod.icon}
                  </div>
                  {produitSelectionne === prod.id && (
                    <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-2">{prod.nom}</h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{prod.description}</p>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Rendement</span>
                    <span className="text-lg font-bold text-green-600">{formatPercentage(prod.rendement)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Risque</span>
                    <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                      prod.risque === 'Faible' ? 'bg-green-100 text-green-700' :
                      prod.risque === 'Modéré' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {prod.risque}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Liquidité</span>
                    <span className="text-sm font-medium text-gray-900">{prod.liquidite}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Avantages :</h4>
                  <ul className="space-y-1">
                    {prod.avantages.map((avantage, idx) => (
                      <li key={idx} className="flex items-center text-xs text-gray-600">
                        <div className="w-1 h-1 bg-purple-500 rounded-full mr-2"></div>
                        {avantage}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Layout: Paramètres à gauche, Résultats et Graphique à droite */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 mb-8">
          {/* Colonne de gauche: Paramètres */}
          <div className="xl:col-span-1">
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 backdrop-blur-sm">
              <div className="border-b border-gray-100 pb-6 mb-8">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 bg-gradient-to-br ${produit.couleur} rounded-xl flex items-center justify-center shadow-lg`}>
                    <Calculator className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Paramètres - {produit.nom}</h2>
                    <p className="text-sm text-gray-500 mt-1">Rendement: {formatPercentage(produit.rendement)}</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                {/* Capital Initial */}
                <div className={`p-4 rounded-xl border bg-gradient-to-r ${produit.couleurBg} border-opacity-50`} style={{ borderColor: produit.couleur.split(' ')[1] }}>
                  <div className="flex items-center space-x-2 mb-3">
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center bg-gradient-to-br ${produit.couleur} bg-opacity-20`}>
                      <DollarSign className="w-3 h-3" style={{ color: produit.couleur.split(' ')[1] }} />
                    </div>
                    <label className="block text-sm font-semibold text-gray-800">
                      Capital Initial
                    </label>
                  </div>
                  <input
                    type="range"
                    min="1000"
                    max="100000"
                    step="1000"
                    value={capitalInitial}
                    onChange={(e) => setCapitalInitial(Number(e.target.value))}
                    className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="mt-3 space-y-2">
                    <input
                      type="number"
                      value={capitalInitial}
                      onChange={(e) => setCapitalInitial(Number(e.target.value))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center font-semibold text-sm bg-white shadow-sm"
                    />
                    <div className="text-center">
                      <div className="text-2xl font-bold" style={{ color: produit.couleur.split(' ')[1] }}>{formatCurrency(capitalInitial)}</div>
                      <div className="text-xs text-gray-500">Montant de départ</div>
                    </div>
                  </div>
                </div>

                {/* Versement Mensuel */}
                <div className={`p-4 rounded-xl border transition-all duration-300 ${
                  avecVersementProgramme 
                    ? `bg-gradient-to-r ${produit.couleurBg} border-opacity-50` 
                    : 'bg-gray-50 border-gray-200 opacity-60'
                }`}>
                  <div className="flex items-center space-x-2 mb-3">
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                      avecVersementProgramme ? `bg-gradient-to-br ${produit.couleur} bg-opacity-20` : 'bg-gray-100'
                    }`}>
                      <TrendingUp className={`w-3 h-3 ${
                        avecVersementProgramme ? '' : 'text-gray-400'
                      }`} style={{ color: avecVersementProgramme ? produit.couleur.split(' ')[1] : undefined }} />
                    </div>
                    <label className="block text-sm font-semibold text-gray-800">
                      Versement Mensuel
                    </label>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="2000"
                    step="50"
                    value={versementMensuel}
                    onChange={(e) => setVersementMensuel(Number(e.target.value))}
                    disabled={!avecVersementProgramme}
                    className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="mt-3 space-y-2">
                    <input
                      type="number"
                      value={versementMensuel}
                      onChange={(e) => setVersementMensuel(Number(e.target.value))}
                      disabled={!avecVersementProgramme}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center font-semibold text-sm bg-white shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${
                        avecVersementProgramme ? '' : 'text-gray-400'
                      }`} style={{ color: avecVersementProgramme ? produit.couleur.split(' ')[1] : undefined }}>{formatCurrency(versementMensuel)}</div>
                      <div className="text-xs text-gray-500">par mois</div>
                    </div>
                  </div>
                </div>

                {/* Durée */}
                <div className={`p-4 rounded-xl border bg-gradient-to-r ${produit.couleurBg} border-opacity-50`}>
                  <div className="flex items-center space-x-2 mb-3">
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center bg-gradient-to-br ${produit.couleur} bg-opacity-20`}>
                      <Target className="w-3 h-3" style={{ color: produit.couleur.split(' ')[1] }} />
                    </div>
                    <label className="block text-sm font-semibold text-gray-800">
                      Durée d'investissement
                    </label>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="30"
                    step="1"
                    value={dureeAnnees}
                    onChange={(e) => setDureeAnnees(Number(e.target.value))}
                    className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="mt-3 space-y-2">
                    <input
                      type="number"
                      value={dureeAnnees}
                      onChange={(e) => setDureeAnnees(Number(e.target.value))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center font-semibold text-sm bg-white shadow-sm"
                    />
                    <div className="text-center">
                      <div className="text-2xl font-bold" style={{ color: produit.couleur.split(' ')[1] }}>{dureeAnnees}</div>
                      <div className="text-xs text-gray-500">année{dureeAnnees > 1 ? 's' : ''}</div>
                    </div>
                  </div>
                </div>

                {/* Toggle Versement */}
                <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-gray-100 rounded-lg flex items-center justify-center">
                        <PieChart className="w-3 h-3 text-gray-600" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-800">Versements programmés</div>
                        <div className="text-xs text-gray-500">Activer les apports mensuels réguliers</div>
                      </div>
                    </div>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={avecVersementProgramme}
                        onChange={(e) => setAvecVersementProgramme(e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`relative inline-flex h-7 w-12 rounded-full transition-all duration-300 ease-in-out shadow-inner ${
                        avecVersementProgramme 
                          ? `bg-gradient-to-r ${produit.couleur} shadow-purple-200` 
                          : 'bg-gray-300 shadow-gray-200'
                      }`}>
                        <div className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-2 ring-white transition-all duration-300 ease-in-out ${
                          avecVersementProgramme ? 'translate-x-6 mt-1' : 'translate-x-1 mt-1'
                        }`} />
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Colonne de droite: Résultats et Graphique */}
          <div className="xl:col-span-2 space-y-6">
            {/* Résultats en cartes */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Capital Investi */}
              <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 relative overflow-hidden backdrop-blur-sm hover:shadow-2xl transition-all duration-300 group">
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${produit.couleur}`}></div>
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 bg-gradient-to-br ${produit.couleur} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300`}>
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Capital Investi</h3>
                <p className="text-2xl font-bold text-gray-900 mb-1">
                  {formatCurrency(totalInvesti)}
                </p>
                <div className="text-xs font-medium bg-gray-50 px-2 py-1 rounded-full inline-block" style={{ color: produit.couleur.split(' ')[1] }}>
                  Montant total
                </div>
              </div>

              {/* Valeur Finale */}
              <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 relative overflow-hidden backdrop-blur-sm hover:shadow-2xl transition-all duration-300 group">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 to-green-500"></div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Valeur Finale</h3>
                <p className="text-2xl font-bold text-gray-900 mb-2">
                  {resultatsFinaux ? formatCurrency(resultatsFinaux.valeur) : '0€'}
                </p>
                <div className="flex items-center space-x-2">
                  <div className="text-xs text-green-600 font-bold bg-green-50 px-2 py-1 rounded-full">
                    +{formatCurrency(gain)}
                  </div>
                  <div className="text-xs text-gray-500">gain</div>
                </div>
              </div>

              {/* Rendement */}
              <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 relative overflow-hidden backdrop-blur-sm hover:shadow-2xl transition-all duration-300 group">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-400 to-purple-500"></div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                </div>
                <h3 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Rendement Annuel</h3>
                <p className="text-2xl font-bold text-gray-900 mb-2">{formatPercentage(produit.rendement)}</p>
                <div className="text-xs text-purple-600 font-medium bg-purple-50 px-2 py-1 rounded-full inline-block">
                  {produit.nom}
                </div>
              </div>
            </div>

            {/* Graphique */}
            <div className="bg-white rounded-2xl p-8 shadow-2xl border border-gray-100 backdrop-blur-sm">
              <div className="border-b border-gray-100 pb-6 mb-8">
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 bg-gradient-to-br ${produit.couleur} rounded-xl flex items-center justify-center shadow-lg`}>
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Évolution - {produit.nom}</h2>
                    <p className="text-sm text-gray-500 mt-1">Projection sur {dureeAnnees} année{dureeAnnees > 1 ? 's' : ''} avec {formatPercentage(produit.rendement)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-6 rounded-xl">
                <ResponsiveContainer width="100%" height={450}>
                  <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis 
                      dataKey="annee" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      label={{ value: 'Années', position: 'insideBottom', offset: -10, style: { textAnchor: 'middle', fill: '#64748b' } }}
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}k€`}
                      label={{ value: 'Valeur (€)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#64748b' } }}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '0.5rem',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                      }}
                      formatter={(value: any, name: string) => [
                        formatCurrency(value),
                        name === 'valeur' ? `Valeur ${produit.nom}` : 'Capital investi'
                      ]}
                      labelFormatter={(label) => `Année ${label}`}
                    />
                    <Legend 
                      wrapperStyle={{
                        paddingTop: '20px'
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="capitalInvesti"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      name="Capital investi"
                      dot={{ fill: '#f59e0b', strokeWidth: 2, r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="valeur"
                      stroke={produit.couleur.includes('blue') ? '#3b82f6' : produit.couleur.includes('emerald') ? '#10b981' : '#f59e0b'}
                      strokeWidth={3}
                      name={`Valeur ${produit.nom}`}
                      dot={{ fill: produit.couleur.includes('blue') ? '#3b82f6' : produit.couleur.includes('emerald') ? '#10b981' : '#f59e0b', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Informations sur le produit sélectionné */}
        <div className={`bg-gradient-to-r ${produit.couleurBg} rounded-2xl p-8 border shadow-xl`} style={{ borderColor: produit.couleur.split(' ')[1], borderWidth: '1px' }}>
          <div className="flex items-start space-x-4">
            <div className={`w-12 h-12 bg-gradient-to-br ${produit.couleur} rounded-xl flex items-center justify-center shadow-lg flex-shrink-0`}>
              <Info className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Informations sur {produit.nom}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-700">
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0`} style={{ backgroundColor: produit.couleur.split(' ')[1] }}></div>
                    <span className="text-sm">Rendement annuel: <strong style={{ color: produit.couleur.split(' ')[1] }}>{formatPercentage(produit.rendement)}</strong></span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm">Niveau de risque: <strong className="text-blue-600">{produit.risque}</strong></span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm">Liquidité: <strong className="text-purple-600">{produit.liquidite}</strong></span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm">Simulation <strong className="text-amber-600">indicative</strong> à titre informatif</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulateurProduits;